import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, Lock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileUploadSection } from "@/components/onboarding/shared/FileUploadSection";
import { ImpactAnalysisSection } from "@/components/steps/impact-analysis-section";
import { PaywallDialog } from "@/components/PaywallDialog";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGetEntityData } from "@/hooks/api/query/useGetEntityData";
import { useUpdateEntityData } from "@/hooks/api/mutate/useUpdateEntityImpactAnalysis";
import { useGeneratePdf } from "@/hooks/api/mutate/useGeneratePdf";
import { useDeductCredits } from "@/hooks/api/mutate/useDeductCredits";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { useToast } from "@/hooks/use-toast";
import { extractDataFromFiles } from "@/lib/file-extraction";
import { getEfectPerturbatorStatus } from "@/utils/guideProgressCalculator";
import {
  prepareImpactAnalysisUpdates,
  mergeWithDefaultImpactData,
} from "@/utils/impact-analysis";
import { generatePDFReport } from "@/utils/generate-report";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import type {
  ImpactAnalysisData,
  LegalRepresentativeData,
} from "@/types/impact-analysis";
import type { ContactData } from "@/types/contact";
import { normalizeCui } from "@/utils/cuiUtils";

export default function ImpactAnalysisPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentSessionId } = useSessionSwitcher();

  const { data, isLoading: isLoadingEntity } =
    useGetEntityData(currentSessionId);
  const entityData = data?.entity_data;
  const reprezentantData = entityData?.contact_data?.reprezentantLegal;

  const updateMutation = useUpdateEntityData();
  const generatePdfMutation = useGeneratePdf();
  const { mutateAsync: deductCredits } = useDeductCredits();

  const {
    data: classifCreditCheck,
    hasInsufficientCredits: hasInsufficientClassifCredits,
  } = useCheckActionCredits(
    {
      session_id: currentSessionId || "",
      action_type: CREDIT_ACTION_TYPES.ANALIZA_EFECT_PERTURBATOR,
    },
    !!currentSessionId
  );

  const {
    data: creditCheck,
    isLoading: isCheckingCredits,
    hasInsufficientCredits,
  } = useCheckActionCredits(
    {
      session_id: currentSessionId || "",
      action_type: CREDIT_ACTION_TYPES.DOC_ANALIZA_EFECT_PERTURBATOR_PDF,
    },
    !!currentSessionId
  );

  const [extractedData, setExtractedData] = useState<ImpactAnalysisData | null>(
    null
  );
  const [extractedCuiRaw, setExtractedCuiRaw] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallActionType, setPaywallActionType] = useState<
    "analiza_efect_perturbator" | "doc_analiza_efect_perturbator_pdf"
  >("doc_analiza_efect_perturbator_pdf");

  const { isVisible } = getEfectPerturbatorStatus(entityData);

  const sessionCui = normalizeCui(entityData?.cui);
  const extractedCui = normalizeCui(extractedCuiRaw);
  const cuiMismatch =
    sessionCui !== "" && extractedCui !== "" && sessionCui !== extractedCui;

  const isLoadingCredits = isCheckingCredits || generatePdfMutation.isPending;
  const isProcessing =
    updateMutation.isPending || generatePdfMutation.isPending;

  const handleUpload = useCallback(async (files: File[]) => {
    setIsExtracting(true);
    setUploadError("");
    setExtractedData(null);
    setExtractedCuiRaw(null);

    try {
      const result = await extractDataFromFiles({
        files,
        documentType: "impact",
      });

      if (!result.isValid || result.errors.length > 0) {
        setUploadError(
          result.errors[0]?.message ?? "Eroare la procesarea fișierului."
        );
        return;
      }

      const impactData = result.mergedData.impactAnalysisData;
      if (!impactData || Object.keys(impactData).length === 0) {
        setUploadError("Nu s-au putut extrage date din fișier.");
        return;
      }

      setExtractedData(mergeWithDefaultImpactData(impactData));
      setExtractedCuiRaw(result.mergedData.entityData?.cui ?? null);
    } catch {
      setUploadError("Eroare la procesarea fișierului.");
    } finally {
      setIsExtracting(false);
    }
  }, []);

  const handleReset = useCallback(() => {
    setExtractedData(null);
    setExtractedCuiRaw(null);
    setUploadError("");
  }, []);

  const saveAnalysisData = (
    analysis: ImpactAnalysisData,
    repData: LegalRepresentativeData
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!currentSessionId) {
        reject(new Error("Nu există o sesiune activă"));
        return;
      }

      const updates = prepareImpactAnalysisUpdates(
        analysis,
        repData,
        entityData?.impact_analysis as ImpactAnalysisData | undefined,
        (entityData?.contact_data || null) as ContactData | null,
        entityData ?? null
      );

      if (!updates) {
        resolve();
        return;
      }

      updateMutation.mutate(
        {
          session_id: currentSessionId,
          updates,
        },
        {
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        }
      );
    });
  };

  const handleAnalysisComplete = async (
    analysis: ImpactAnalysisData,
    repData: LegalRepresentativeData
  ) => {
    if (!currentSessionId) {
      toast({
        title: "Eroare",
        description: "Nu există o sesiune activă",
        variant: "destructive",
      });
      return;
    }

    const updates = prepareImpactAnalysisUpdates(
      analysis,
      repData,
      entityData?.impact_analysis as ImpactAnalysisData | undefined,
      (entityData?.contact_data || null) as ContactData | null,
      entityData ?? null
    );

    if (!updates) {
      toast({
        title: "Informare",
        description: "Nu există modificări de salvat",
      });
      navigate("/dashboard");
      return;
    }

    // Deduct credits for EP classification if not already purchased
    if (!classifCreditCheck?.already_purchased) {
      if (!classifCreditCheck?.has_credits) {
        setPaywallActionType("analiza_efect_perturbator");
        setShowPaywall(true);
        return;
      }
      try {
        await deductCredits({
          action_type: CREDIT_ACTION_TYPES.ANALIZA_EFECT_PERTURBATOR,
          session_id: currentSessionId,
        });
      } catch (error) {
        if (error instanceof InsufficientCreditsError) {
          setPaywallActionType("analiza_efect_perturbator");
          setShowPaywall(true);
          return;
        }
        toast({
          title: "Eroare",
          description: "Nu s-au putut deduce creditele.",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      await saveAnalysisData(analysis, repData);
      toast({
        title: "Salvat cu succes",
        description: "Analiza de impact a fost salvată",
      });
      navigate("/dashboard");
    } catch (error) {
      console.error("Error saving impact analysis:", error);
      toast({
        title: "Eroare la salvare",
        description: "Nu s-a putut salva analiza de impact",
        variant: "destructive",
      });
    }
  };

  const handleDownloadPdf = async (
    analysis: ImpactAnalysisData,
    repData: LegalRepresentativeData
  ) => {
    if (!currentSessionId) {
      toast({
        title: "Eroare",
        description: "Nu există o sesiune activă",
        variant: "destructive",
      });
      return;
    }

    if (hasInsufficientCredits) {
      setPaywallActionType("doc_analiza_efect_perturbator_pdf");
      setShowPaywall(true);
      return;
    }

    try {
      await saveAnalysisData(analysis, repData);

      generatePdfMutation.mutate(
        { sessionId: currentSessionId, type: "impactAnalysis" },
        {
          onSuccess: async (data) => {
            await generatePDFReport(
              data as unknown as {
                template: unknown[];
                inputs: unknown[];
                type: "merge" | undefined;
              }
            );
          },
          onError: (error) => {
            if (error instanceof InsufficientCreditsError) {
              setShowPaywall(true);
              return;
            }
            console.error("Error generating PDF:", error);
            toast({
              title: "Eroare",
              description: "PDF-ul nu a putut fi generat. Încercați din nou.",
              variant: "destructive",
            });
          },
        }
      );
    } catch (error) {
      console.error("Error saving before PDF generation:", error);
      toast({
        title: "Eroare la salvare",
        description: "Nu s-a putut salva analiza de impact",
        variant: "destructive",
      });
    }
  };

  const handleSkipAnalysis = () => {
    toast({
      title: "Analiză omisă",
      description: "Puteți reveni oricând pentru a completa analiza",
    });
    navigate("/dashboard");
  };

  if (isLoadingEntity) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!isVisible) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-800">
            Secțiune indisponibilă
          </h2>
          <p className="text-sm text-slate-500 max-w-sm">
            Analiza efect perturbator este disponibilă doar pentru entitățile
            clasificate cu impact perturbator (Art. 9).
          </p>
          <Button onClick={() => navigate("/dashboard")} className="mt-2">
            Înapoi la Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Activity className="h-8 w-8 text-primary" />
            <h1 className="font-heading text-3xl font-bold">
              Încărcare Analiză Efect Perturbator
            </h1>
          </div>
          <p className="text-lg text-muted-foreground">
            Încarcă fișierul XLSX cu analiza efect perturbator completată
            conform art. 9 din OUG 155/2024.
          </p>
        </div>

        {!extractedData && (
          <>
            <FileUploadSection
              onUpload={handleUpload}
              isExtracting={isExtracting}
              accept=".xlsx,.xls"
            />

            {uploadError && (
              <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-lg">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {uploadError}
              </div>
            )}
          </>
        )}

        {extractedData && (
          <>
            {cuiMismatch && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    CUI diferit detectat
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    CUI-ul din fișier ({extractedCuiRaw}) nu corespunde cu
                    CUI-ul entității curente ({entityData?.cui}). Încărcați un
                    fișier pentru entitatea corectă.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={isProcessing}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Schimbă fișierul
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>
                  {entityData?.nume_entitate} - {entityData?.cui}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-4 md:pt-0 md:pb-6">
                {cuiMismatch ? (
                  <div className="flex items-center justify-center py-12 text-muted-foreground">
                    <p>
                      Rezolvați nepotrivirea CUI pentru a putea edita analiza.
                    </p>
                  </div>
                ) : (
                  <ImpactAnalysisSection
                    impactAnalysis={extractedData}
                    reprezentantData={reprezentantData}
                    onAnalysisComplete={handleAnalysisComplete}
                    onSkipAnalysis={handleSkipAnalysis}
                    onDownloadPdf={handleDownloadPdf}
                    isProcessing={isProcessing}
                    hasInsufficientCredits={hasInsufficientCredits}
                    hasInsufficientClassifCredits={
                      hasInsufficientClassifCredits
                    }
                    isLoadingCredits={isLoadingCredits}
                  />
                )}
              </CardContent>
            </Card>
          </>
        )}

        {extractedData && !cuiMismatch && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <h3 className="font-semibold">Despre această analiză</h3>
                <p className="text-sm text-muted-foreground">
                  Această analiză este parte integrantă a procesului de evaluare
                  NIS2 și este utilizată pentru a determina nivelul de impact al
                  entității dumneavoastră. Rezultatele vor fi incluse automat în
                  raportul de clasificare.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <PaywallDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        actionType={paywallActionType}
        onPurchase={(packageId) => {
          toast({
            title: "Redirecționare către plată",
            description: `Vei fi redirecționat către Stripe pentru pachetul ${packageId}`,
          });
        }}
      />
    </div>
  );
}
